import { useCallback, useMemo } from 'react';
import { getBrowserLogger } from '../../browser-logger';
import { getSessionTrackingKey } from '../../browser-store';
import { ActionType, ItemType } from '../../types/messaging-types';
import { getEncryptedConversationId } from '../utils/conversationIdUtils';
import type { SdkLoggingFunctions } from '@indeed/messaging-react';

type CustomLogOpts = {
    additionalParams?: Record<string, unknown>;
    elementIndex?: string | number;
    itemType?: string;
    itemsAttributes?: Record<string, unknown>;
    tags?: {
        itemUids?: string[];
        [key: string]: unknown;
    };
};

export type LoggingFunctionsHelpers = Required<SdkLoggingFunctions> & {
    atk: string;
};

const LOGGING_ATK_NOT_FOUND = 'MSGW_ATK_NOT_FOUND';

/**
 * Builds additionalParams from CustomLogOpts
 * Only returns additionalParams if explicitly provided
 * Encrypts conversationId values if present
 */
const buildAdditionalParams = (options?: CustomLogOpts): Record<string, unknown> | undefined => {
    if (!options || options.additionalParams === undefined) {
        return undefined;
    }

    const params = options.additionalParams as Record<string, unknown>;

    // Encrypt conversationId (singular) if present
    if (params.conversationId && typeof params.conversationId === 'string') {
        const { conversationId, ...rest } = params;
        return {
            ...rest,
            conversationSid: getEncryptedConversationId(conversationId),
        };
    }

    return params;
};

/**
 * Hook that provides logging functions for the new logging architecture.
 */
const useLoggingFunctions = (): LoggingFunctionsHelpers => {
    // Get browser logger (may fail in Storybook/test environments)
    let browserLogger: {
        logUserAction: (log: any) => void;
        logImpression: (log: any) => void;
        logCaughtError: (e: Error) => void;
    };
    try {
        browserLogger = getBrowserLogger();
    } catch (e) {
        console.warn('Browser logger not initialized. Using no-op logger.', e);
        browserLogger = {
            logUserAction: () => {},
            logImpression: () => {},
            logCaughtError: () => {},
        };
    }

    const getElementString = (element?: string, elementIndex?: string | number): string => {
        if (!element) {
            return '';
        }
        const indexString = elementIndex == null ? '' : `[${elementIndex}]`;
        return `'${element}${indexString}'`;
    };

    // Get pageload tracking key
    let pageloadTk = '';
    try {
        pageloadTk = getSessionTrackingKey().pageloadTk;
    } catch (e) {
        console.warn('Browser store not initialized. Falling back to default atk.', e);
        pageloadTk = '';
    }

    const logClick = useCallback(
        (element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            const additionalParams = buildAdditionalParams(opts);
            const logPayload: any = {
                action: ActionType.CLICK,
                itemUid: getElementString(element, opts?.elementIndex),
                itemType: ItemType.BUTTON,
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logUserAction(logPayload);
        },
        [browserLogger]
    );

    const logImpression = useCallback(
        (element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            // Extract and encrypt conversationIds if present in additionalParams
            let conversationSids: string[] | undefined;
            let additionalParamsToUse = opts?.additionalParams as
                | Record<string, unknown>
                | undefined;

            if (
                additionalParamsToUse &&
                'conversationIds' in additionalParamsToUse &&
                Array.isArray(additionalParamsToUse.conversationIds)
            ) {
                const { conversationIds, ...rest } = additionalParamsToUse;
                conversationSids = conversationIds.map((id: string) =>
                    getEncryptedConversationId(id)
                );
                additionalParamsToUse = Object.keys(rest).length > 0 ? rest : undefined;
            }

            const additionalParams = buildAdditionalParams({
                ...opts,
                additionalParams: additionalParamsToUse,
            });

            const logPayload: any = {
                itemUids: opts?.tags?.itemUids ?? conversationSids ?? [element],
                itemType: opts?.itemType ?? element ?? ItemType.MESSAGES,
                itemsAttributes: opts?.itemsAttributes ?? {},
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logImpression(logPayload);
        },
        [browserLogger]
    );

    const logCustomAction = useCallback(
        (customActionType: string, element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            const additionalParams = buildAdditionalParams(opts);

            const logPayload: any = {
                action: customActionType,
                itemUid: opts?.elementIndex ?? element,
                itemType: opts?.itemType ?? element,
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logUserAction(logPayload);
        },
        [browserLogger]
    );

    const logFailure = useCallback(
        (element: string, errorOptions?: Record<string, unknown>) => {
            const errorMessage =
                errorOptions?.error != null
                    ? `${element} - ${String(errorOptions.error)}`
                    : element;
            browserLogger.logCaughtError(new Error(errorMessage));
        },
        [browserLogger]
    );

    return useMemo(
        () => ({
            atk: pageloadTk || LOGGING_ATK_NOT_FOUND,
            logClick,
            logImpression,
            logCustomAction,
            logFailure,
        }),
        [pageloadTk, logClick, logImpression, logCustomAction, logFailure]
    );
};

export default useLoggingFunctions;
